/**
 * Start a download from a legacy 360 presigned S3 URL.
 *
 * We intentionally do NOT use `fetch()`: cross-origin GETs to S3 require bucket CORS
 * exposing the origin (e.g. localhost). A top-level navigation / `<a>` click is not
 * subject to that restriction; the object’s `Content-Disposition` (set on the presigned
 * URL) drives the save-as name in the browser.
 *
 * The `serverFilename` basename is passed to `download` for same-origin-friendly UAs;
 * for cross-origin S3, browsers typically ignore `download` and use the response header.
 */

function downloadBasenameOnly(
  name: string | undefined,
  presignedUrl: string
): string {
  const fromName = (raw: string): string | null => {
    const normalized = raw.replace(/\\/g, "/");
    const leaf = normalized.split("/").pop() ?? normalized;
    const trimmed = leaf.replace(/^\.+/, "").trim();
    return trimmed || null;
  };
  if (name && typeof name === "string") {
    const leaf = fromName(name);
    if (leaf) return leaf;
  }
  try {
    const pathLeaf = new URL(presignedUrl).pathname
      .split("/")
      .filter(Boolean)
      .pop();
    if (pathLeaf) {
      const leaf = fromName(pathLeaf);
      if (leaf) return leaf;
    }
  } catch {
    /* invalid URL — fall through */
  }
  return "download";
}

export function downloadLegacy360PresignedFile(
  presignedUrl: string,
  serverFilename: string | undefined
): void {
  const link = document.createElement("a");
  link.href = presignedUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = downloadBasenameOnly(serverFilename, presignedUrl);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
